import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import { useAuth } from "./useAuth.hook";
import { useUserStartups, useCreateStartup } from "./useFinancialData";
import { CreateStartupModal } from "@/components/molecules/CreateStartupModal";
import { ProUpgradeModal } from "@/components/molecules/ProUpgradeFunnel";
import { trackStartupCreated } from "@/services/firebase/analytics.service";
import { useProAccess } from "@/hooks/useProAccess";
import { FREE_TIER_MAX_OWNED_STARTUPS } from "@/constants/pro-features";
import type { CreateStartupData, Startup } from "@/types/startup.types";

interface StartupContextType {
  currentStartup: Startup | null;
  availableStartups: Startup[];
  ownedStartups: Startup[];
  sharedStartups: Startup[];
  isLoading: boolean;
  error: Error | null;
  selectStartup: (startupId: string) => void;
  canAccessStartup: (startup: Startup, userId: string) => boolean;
  openCreateStartupModal: () => void;
  isCreatingStartup: boolean;
}

const StartupContext = createContext<StartupContextType | undefined>(undefined);

export function useStartupContext() {
  const context = useContext(StartupContext);
  if (context === undefined) {
    throw new Error("useStartupContext must be used within a StartupProvider");
  }
  return context;
}

interface StartupProviderProps {
  children: ReactNode;
}

function isOwnedStartup(startup: Startup, userId: string): boolean {
  if (startup.accessType === "owned") return true;
  if (startup.accessType === "shared") return false;
  return startup.ownerId === userId;
}

export function StartupProvider({ children }: StartupProviderProps) {
  const { firebaseUser } = useAuth();
  const { isPro } = useProAccess();
  const [currentStartup, setCurrentStartup] = useState<Startup | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isStartupLimitPaywallOpen, setIsStartupLimitPaywallOpen] = useState(false);

  const {
    data: allStartups = [],
    isLoading,
    error,
  } = useUserStartups(firebaseUser ?? undefined);

  const createStartupMutation = useCreateStartup(firebaseUser?.uid);

  const canAccessStartup = useCallback(
    (startup: Startup, userId: string): boolean => {
      if (startup.ownerId === userId) {
        return true;
      }

      if (startup.members?.some((member) => member.userId === userId)) {
        return true;
      }

      if (startup.memberIds?.includes(userId)) {
        return true;
      }

      return false;
    },
    [],
  );

  const availableStartups = useMemo(
    () =>
      allStartups.filter((startup) =>
        firebaseUser ? canAccessStartup(startup, firebaseUser.uid) : false,
      ),
    [allStartups, firebaseUser, canAccessStartup],
  );

  const ownedStartups = useMemo(
    () =>
      firebaseUser
        ? availableStartups.filter((startup) =>
            isOwnedStartup(startup, firebaseUser.uid),
          )
        : [],
    [availableStartups, firebaseUser],
  );

  const sharedStartups = useMemo(
    () =>
      firebaseUser
        ? availableStartups.filter(
            (startup) => !isOwnedStartup(startup, firebaseUser.uid),
          )
        : [],
    [availableStartups, firebaseUser],
  );

  useEffect(() => {
    if (!firebaseUser || isLoading || availableStartups.length === 0) {
      setCurrentStartup(null);
      return;
    }

    if (
      !currentStartup ||
      !canAccessStartup(currentStartup, firebaseUser.uid)
    ) {
      const lastSelectedStartupId = localStorage.getItem(
        `lastSelectedStartup_${firebaseUser.uid}`,
      );

      let startupToSelect: Startup | null = null;

      if (lastSelectedStartupId) {
        startupToSelect =
          availableStartups.find((s) => s.id === lastSelectedStartupId) || null;
      }

      if (!startupToSelect && availableStartups.length > 0) {
        startupToSelect = availableStartups[0];
      }

      if (startupToSelect) {
        setCurrentStartup(startupToSelect);
        localStorage.setItem(
          `lastSelectedStartup_${firebaseUser.uid}`,
          startupToSelect.id,
        );
      }
    }
  }, [firebaseUser, availableStartups, isLoading, currentStartup, canAccessStartup]);

  const selectStartup = useCallback(
    (startupId: string) => {
      if (!firebaseUser) return;

      const startup = availableStartups.find((s) => s.id === startupId);
      if (startup && canAccessStartup(startup, firebaseUser.uid)) {
        setCurrentStartup(startup);
        localStorage.setItem(
          `lastSelectedStartup_${firebaseUser.uid}`,
          startupId,
        );
      }
    },
    [firebaseUser, availableStartups, canAccessStartup],
  );

  const handleCreateStartup = useCallback(
    async (data: CreateStartupData) => {
      const startup = await createStartupMutation.mutateAsync(data);
      void trackStartupCreated("dashboard");
      setCurrentStartup(startup);
      if (firebaseUser) {
        localStorage.setItem(
          `lastSelectedStartup_${firebaseUser.uid}`,
          startup.id,
        );
      }
    },
    [createStartupMutation, firebaseUser],
  );

  const openCreateStartupModal = useCallback(() => {
    if (
      ownedStartups.length >= FREE_TIER_MAX_OWNED_STARTUPS &&
      !isPro
    ) {
      setIsStartupLimitPaywallOpen(true);
      return;
    }
    setIsCreateModalOpen(true);
  }, [ownedStartups.length, isPro]);

  useEffect(() => {
    if (!firebaseUser) {
      setCurrentStartup(null);
    }
  }, [firebaseUser]);

  const value: StartupContextType = {
    currentStartup,
    availableStartups,
    ownedStartups,
    sharedStartups,
    isLoading,
    error: error as Error | null,
    selectStartup,
    canAccessStartup,
    openCreateStartupModal,
    isCreatingStartup: createStartupMutation.isPending,
  };

  return (
    <StartupContext.Provider value={value}>
      {children}
      <CreateStartupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateStartup}
        isLoading={createStartupMutation.isPending}
      />
      <ProUpgradeModal
        isOpen={isStartupLimitPaywallOpen}
        onClose={() => setIsStartupLimitPaywallOpen(false)}
        featureId="multiple_startups"
        title="Additional startups require Pro"
        description="Your first burn chart is free. Upgrade to create and manage multiple startups on one account."
        showFeatureGrid={false}
      />
    </StartupContext.Provider>
  );
}
